import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../../i18n';
import { api } from '../../services/api';
import useModalEscape from './useModalEscape';

const DISCOVER_LIMIT = 10;
const SEARCH_DEBOUNCE_MS = 1000;

function detectPlatformFromInput(value) {
  const raw = String(value || '').trim().toLowerCase();
  if (raw.includes('tiktok.com')) return 'tiktok';
  if (raw.includes('instagram.com') || raw.includes('instagr.am')) return 'instagram';
  return null;
}

function parseSocialInput(value, fallbackPlatform = 'instagram') {
  const raw = String(value || '').trim();
  if (!raw) return null;

  let platform = fallbackPlatform;
  let handle = raw;

  try {
    const withProtocol = raw.startsWith('http') ? raw : `https://${raw}`;
    const url = new URL(withProtocol);
    const host = url.hostname.replace(/^www\./, '');
    const parts = url.pathname.split('/').filter(Boolean);

    if (host.includes('instagram.com')) {
      platform = 'instagram';
      handle = parts[0] || '';
    } else if (host.includes('tiktok.com')) {
      platform = 'tiktok';
      handle = (parts[0] || '').replace(/^@/, '');
    }
  } catch {
    handle = raw.replace(/^@/, '');
  }

  handle = handle
    .replace(/^@/, '')
    .split(/[/?#]/)[0]
    .trim();

  if (!handle) return null;

  return {
    platform,
    handle,
    name: handle,
    display_name: handle,
  };
}

function avatarImageUrl(url) {
  if (!url) return '';
  const value = String(url);
  if (value.startsWith('/media/') || value.startsWith('/outfit-detective/') || value.includes('/outfit-detective/')) return value;
  return `/api/media/avatar?url=${encodeURIComponent(url)}`;
}

const Discover = () => {
  const { formatDate, formatNumber, t } = useI18n();
  const [follows, setFollows] = useState([]);
  const [socialSearch, setSocialSearch] = useState({ platform: 'instagram', query: '' });
  const [socialResults, setSocialResults] = useState([]);
  const [socialSearching, setSocialSearching] = useState(false);
  const [socialSearchPending, setSocialSearchPending] = useState(false);
  const [socialHasMore, setSocialHasMore] = useState(false);
  const [socialOffset, setSocialOffset] = useState(0);
  const [socialLoadingMore, setSocialLoadingMore] = useState(false);
  const [message, setMessage] = useState('');
  const [profileInput, setProfileInput] = useState('');
  const [pastePlatform, setPastePlatform] = useState('instagram');
  const [profileChecking, setProfileChecking] = useState(false);
  const [bulkInput, setBulkInput] = useState('');
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [previewProfile, setPreviewProfile] = useState(null);
  const [previewDetail, setPreviewDetail] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [blockedFollowProfile, setBlockedFollowProfile] = useState(null);
  const loadMoreRef = useRef(null);
  const searchRequestRef = useRef(0);
  useModalEscape(Boolean(previewProfile), () => setPreviewProfile(null));
  useModalEscape(Boolean(blockedFollowProfile), () => setBlockedFollowProfile(null));

  const loadFollows = useCallback(async () => {
    const res = await api.getFollows();
    setFollows(res.data || []);
  }, []);

  useEffect(() => {
    loadFollows();
  }, [loadFollows]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const profileUrl = params.get('profileUrl');

    if (profileUrl) {
      setProfileInput(profileUrl);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const loadSocialResults = useCallback(
    async (nextOffset = 0, append = false) => {
      const query = socialSearch.query.trim();
      if (query.length < 2) return;
      const requestId = searchRequestRef.current + 1;
      searchRequestRef.current = requestId;

      if (append) {
        setSocialLoadingMore(true);
      } else {
        setSocialSearching(true);
      }

      try {
        const res = await api.discoverSocialProfiles(socialSearch.platform, query, {
          limit: DISCOVER_LIMIT,
          offset: nextOffset,
          verifiedOnly: socialSearch.platform === 'tiktok',
        });
        const nextResults = res.data || [];
        if (requestId !== searchRequestRef.current) return;
        setSocialResults((current) => (append ? [...current, ...nextResults] : nextResults));
        setSocialHasMore(Boolean(res.meta?.has_more));
        setSocialOffset(res.meta?.next_offset || nextOffset + DISCOVER_LIMIT);
      } catch {
        if (requestId === searchRequestRef.current) {
          if (!append) setSocialResults([]);
          setSocialHasMore(false);
        }
      } finally {
        if (requestId === searchRequestRef.current) {
          setSocialSearching(false);
          setSocialLoadingMore(false);
        }
      }
    },
    [socialSearch.platform, socialSearch.query],
  );

  useEffect(() => {
    let mounted = true;
    const query = socialSearch.query.trim();

    if (query.length < 2) {
      searchRequestRef.current += 1;
      setSocialResults([]);
      setSocialHasMore(false);
      setSocialOffset(0);
      setSocialSearching(false);
      setSocialSearchPending(false);
      return undefined;
    }

    setSocialSearchPending(true);
    const timer = setTimeout(() => {
      if (!mounted) return;
      setSocialSearchPending(false);
      loadSocialResults(0, false);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      mounted = false;
      clearTimeout(timer);
    };
  }, [loadSocialResults, socialSearch.platform, socialSearch.query]);

  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node || !socialHasMore || socialSearching || socialLoadingMore) return undefined;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) {
        loadSocialResults(socialOffset, true);
      }
    });

    observer.observe(node);
    return () => observer.disconnect();
  }, [loadSocialResults, socialHasMore, socialSearching, socialLoadingMore, socialOffset]);

  async function followSocialResult(result) {
    const name = result.celebrity?.name || result.display_name || result.handle;
    const payload = {
      name,
      tier: 'C',
      [result.platform]: {
        handle: result.handle,
        display_name: result.display_name,
        profile_url: result.profile_url,
        avatar_url: result.avatar_url,
        bio: result.bio,
        follower_count: result.follower_count,
        is_verified: result.is_verified,
        status: result.status === 'approved' ? 'approved' : 'candidate',
      },
    };

    try {
      const res = await api.pickAndFollowCelebrity(payload);
      const celebrityName = res.data?.celebrity?.name || name;
      setMessage(t('discover.added', { name: celebrityName }));
      setPreviewProfile(null);
      setPreviewDetail(null);
      setSocialSearch({ platform: result.platform, query: '' });
      setSocialResults([]);
      setProfileInput('');
      await loadFollows();
    } catch (error) {
      if (error?.message === 'celebrity_banned' || error?.payload?.error === 'celebrity_banned') {
        const bannedProfile = error.payload?.banned_profile || {};
        setBlockedFollowProfile({
          ...result,
          name: bannedProfile.name || name,
          slug: bannedProfile.slug || result.celebrity?.slug || '',
          display_name: result.display_name || bannedProfile.name || name,
          handle: result.handle || '',
          platform: result.platform,
          profile_url: result.profile_url || getProfileUrl(result),
          avatar_url: result.avatar_url,
          follower_count: result.follower_count,
          is_verified: result.is_verified,
        });
        setMessage('');
        return;
      }

      setMessage(t('discover.followError'));
    }
  }

  async function addProfileFromInput(event) {
    event.preventDefault();
    const parsed = parseSocialInput(profileInput, pastePlatform);

    if (!parsed) return;

    const candidate = {
      type: 'candidate',
      platform: parsed.platform,
      handle: parsed.handle,
      display_name: parsed.display_name,
      profile_url: getProfileUrl(parsed),
      is_verified: false,
      verification_status: 'needs_review',
      status: 'candidate',
      source: 'pasted_profile',
    };

    setProfileChecking(true);
    setPreviewProfile(candidate);
    setPreviewDetail(null);
    setPreviewLoading(true);
    setMessage('');

    try {
      const res = await api.getSocialProfileDetail(candidate.platform, candidate.handle);
      setPreviewDetail(res.data || null);
    } catch {
      setPreviewProfile(null);
      setMessage(t('discover.profilePreviewFailed'));
    } finally {
      setPreviewLoading(false);
      setProfileChecking(false);
    }
  }

  function updateProfileInput(value) {
    setProfileInput(value);
    const detectedPlatform = detectPlatformFromInput(value);
    if (detectedPlatform) setPastePlatform(detectedPlatform);
  }

  async function importBulkList(event) {
    event.preventDefault();
    const parsedItems = bulkInput
      .split(/[\n,;]/)
      .map((item) => parseSocialInput(item, socialSearch.platform))
      .filter(Boolean);

    const uniqueItems = Array.from(
      new Map(parsedItems.map((item) => [`${item.platform}:${item.handle.toLowerCase()}`, item])).values(),
    );

    const selectedItems = uniqueItems.slice(0, 25);

    for (const item of selectedItems) {
      await followSocialResult({
        type: 'candidate',
        platform: item.platform,
        handle: item.handle,
        display_name: item.display_name,
        is_verified: false,
        status: 'candidate',
      });
    }

    setBulkInput('');
    setMessage(t('discover.bulkAdded', { count: selectedItems.length }));
  }

  async function removeFollow(celebrityId) {
    try {
      await api.unfollowCelebrity(celebrityId);
      await loadFollows();
      setMessage(t('discover.removed'));
    } catch (err) {
      setMessage(t('discover.removeError'));
    }
  }

  function getFollowForProfile(profile) {
    const celebrityId = Number(profile?.celebrity?.id || profile?.celebrity_id || profile?.id);
    if (celebrityId) {
      const byId = follows.find((item) => Number(item.id) === celebrityId);
      if (byId) return byId;
    }

    const platform = String(profile?.platform || '').toLowerCase();
    const handle = String(profile?.handle || '').replace(/^@/, '').toLowerCase();
    if (!platform || !handle) return null;

    return follows.find((item) => (
      String(item.platform || '').toLowerCase() === platform &&
      String(item.handle || '').replace(/^@/, '').toLowerCase() === handle
    )) || null;
  }

  async function removeFollowFromPreview(celebrityId) {
    await removeFollow(celebrityId);
    setPreviewProfile(null);
    setPreviewDetail(null);
  }

  function getProfileUrl(result) {
    return (
      result.profile_url ||
      (result.platform === 'tiktok'
        ? `https://www.tiktok.com/@${result.handle}`
        : `https://www.instagram.com/${result.handle}/`)
    );
  }

  function formatFollowers(count) {
    return count ? t('discover.followers', { count: formatNumber(count) }) : t('discover.followersUnavailable');
  }

  function formatCompactNumber(value) {
    if (!value) return 'n/d';
    return formatNumber(value);
  }

  function formatDateLabel(value, fallback) {
    if (fallback) return fallback;
    if (!value) return 'n/d';
    return formatDate(value, { month: 'short', year: 'numeric' });
  }

  async function openProfilePreview(result) {
    setPreviewProfile(result);
    setPreviewDetail(null);
    setPreviewLoading(true);

    try {
      const res = await api.getSocialProfileDetail(result.platform, result.handle);
      setPreviewDetail(res.data || null);
    } catch {
      setPreviewDetail(null);
    } finally {
      setPreviewLoading(false);
    }
  }

  function renderPreviewValue(value, formatter = (item) => item) {
    if (value) return formatter(value);
    if (previewLoading) return <span className="ct-preview-skeleton" aria-label={t('common.loading')}></span>;
    return 'n/d';
  }

  function renderSocialResult(result) {
    const displayName = result.display_name || result.celebrity?.name || result.handle;
    const profileUrl = getProfileUrl(result);
    const followed = getFollowForProfile(result);

    return (
      <article className="ct-social-result" key={`${result.type}-${result.platform}-${result.id || result.handle}`}>
        <span className="ct-result-avatar">
          {result.avatar_url ? (
            <img
              src={avatarImageUrl(result.avatar_url)}
              alt=""
              onError={(event) => { event.currentTarget.style.display = 'none'; }}
            />
          ) : null}
          <span>{(displayName || '?').slice(0, 1).toUpperCase()}</span>
        </span>
        <span className="ct-result-copy">
          <strong>
            {displayName}
            {result.is_verified ? <i className="ph-fill ph-seal-check"></i> : null}
          </strong>
          <em>
            {result.platform} @{result.handle}
          </em>
          <span className="ct-result-preview">
            {result.follower_count ? t('discover.followers', { count: formatNumber(result.follower_count) }) : null}
            {result.follower_count && result.verification_status ? ' · ' : null}
            {result.is_verified ? t('discover.verifiedBadge') : t('discover.publicProfile')}
          </span>
        </span>
        <span className="ct-result-actions">
          <button type="button" className="ct-result-preview-btn" onClick={() => openProfilePreview(result)}>
            {t('common.preview')}
          </button>
          <a href={profileUrl} target="_blank" rel="noreferrer">
            {t('common.open')}
          </a>
          {followed ? (
            <Link to={`/following/${followed.slug}`}>
              {t('discover.openProfile')}
            </Link>
          ) : (
            <button type="button" onClick={() => followSocialResult(result)}>
              {t('common.follow')}
            </button>
          )}
        </span>
      </article>
    );
  }

  function renderProfilePreview() {
    if (!previewProfile) return null;

    const detail = { ...previewProfile, ...previewDetail };
    const displayName = detail.display_name || detail.celebrity?.name || detail.handle;
    const profileUrl = getProfileUrl(detail);
    const followed = getFollowForProfile(detail);

    return (
      <div className="ct-preview-overlay" role="presentation" onClick={() => setPreviewProfile(null)}>
        <section
          className="ct-preview-dialog"
          role="dialog"
          aria-modal="true"
          aria-label={`${t('common.preview')} ${displayName}`}
          onClick={(event) => event.stopPropagation()}
        >
          <button type="button" className="ct-preview-close" onClick={() => setPreviewProfile(null)} aria-label={t('discover.closePreview')}>
            <i className="ph ph-x"></i>
          </button>
          <div className="ct-preview-main">
            <span className="ct-preview-avatar">
              {detail.avatar_url ? (
                <img
                  src={avatarImageUrl(detail.avatar_url)}
                  alt=""
                  onError={(event) => { event.currentTarget.style.display = 'none'; }}
                />
              ) : null}
              <span>{(displayName || '?').slice(0, 1).toUpperCase()}</span>
            </span>
            <div className="ct-preview-copy">
              <span className="ct-preview-platform">{detail.platform}</span>
              <h2>
                {displayName}
                {detail.is_verified ? <i className="ph-fill ph-seal-check"></i> : null}
              </h2>
              <p>@{detail.handle}</p>
            </div>
          </div>
          {previewLoading ? (
            <p className="ct-preview-loading">
              <span className="ct-preview-loading-spinner" aria-hidden="true"></span>
              {t('discover.profileDetailsLoading')}
            </p>
          ) : null}
          <div className={`ct-preview-stats${previewLoading ? ' is-loading' : ''}`}>
            <span className={previewLoading ? 'loading' : ''}>
              <strong>{renderPreviewValue(detail.follower_count, formatFollowers)}</strong>
              <em>{t('discover.audience')}</em>
            </span>
            <span className={previewLoading ? 'loading' : ''}>
              <strong>{renderPreviewValue(detail.following_count, formatCompactNumber)}</strong>
              <em>Following</em>
            </span>
            <span className={previewLoading ? 'loading' : ''}>
              <strong>{renderPreviewValue(detail.post_count, formatCompactNumber)}</strong>
              <em>{detail.platform === 'tiktok' ? t('discover.videos') : t('discover.posts')}</em>
            </span>
            {detail.platform === 'tiktok' ? (
              <span className={previewLoading ? 'loading' : ''}>
                <strong>{renderPreviewValue(detail.like_count, formatCompactNumber)}</strong>
                <em>{t('discover.totalLikes')}</em>
              </span>
            ) : null}
            <span className={previewLoading ? 'loading' : ''}>
              <strong>{renderPreviewValue(detail.joined_label || detail.joined_at, () => formatDateLabel(detail.joined_at, detail.joined_label))}</strong>
              <em>{t('discover.onPlatformSince', { platform: detail.platform === 'tiktok' ? 'TikTok' : 'Instagram' })}</em>
            </span>
            {(detail.country || previewLoading) ? (
              <span className={previewLoading ? 'loading' : ''}>
                <strong>{renderPreviewValue(detail.country)}</strong>
                <em>{t('discover.country')}</em>
              </span>
            ) : null}
            {detail.latest_activity_at ? (
              <span>
                <strong>{formatDateLabel(detail.latest_activity_at)}</strong>
                <em>{t('discover.latestActivity')}</em>
              </span>
            ) : null}
          </div>
          {detail.bio ? <p className="ct-preview-bio">{detail.bio}</p> : null}
          {detail.external_url ? (
            <a className="ct-preview-link" href={detail.external_url} target="_blank" rel="noreferrer">
              {detail.external_url.replace(/^https?:\/\//, '')}
            </a>
          ) : null}
          <div className="ct-preview-actions">
            {followed ? (
              <>
                <Link to={`/following/${followed.slug}`} onClick={() => setPreviewProfile(null)}>
                  {t('discover.openProfile')}
                </Link>
                <button type="button" onClick={() => removeFollowFromPreview(followed.id)}>
                  {t('common.unfollow')}
                </button>
              </>
            ) : (
              <>
                <a href={profileUrl} target="_blank" rel="noreferrer">
                  {t('discover.openProfile')}
                </a>
                <button type="button" onClick={() => followSocialResult(detail)}>
                  {t('common.follow')}
                </button>
              </>
            )}
          </div>
        </section>
      </div>
    );
  }

  function renderBlockedFollowDialog() {
    if (!blockedFollowProfile) return null;

    const displayName = blockedFollowProfile.display_name || blockedFollowProfile.name || blockedFollowProfile.handle;
    const profileUrl = getProfileUrl(blockedFollowProfile);

    return (
      <div className="ct-preview-overlay" role="presentation" onClick={() => setBlockedFollowProfile(null)}>
        <section
          className="ct-preview-dialog ct-rights-dialog"
          role="dialog"
          aria-modal="true"
          aria-label={t('discover.blockedTitle')}
          onClick={(event) => event.stopPropagation()}
        >
          <button type="button" className="ct-preview-close" onClick={() => setBlockedFollowProfile(null)} aria-label={t('discover.blockedClose')}>
            <i className="ph ph-x"></i>
          </button>

          <div className="ct-preview-main">
            <span className="ct-preview-avatar ct-rights-avatar">
              {blockedFollowProfile.avatar_url ? (
                <img
                  src={avatarImageUrl(blockedFollowProfile.avatar_url)}
                  alt=""
                  onError={(event) => { event.currentTarget.style.display = 'none'; }}
                />
              ) : null}
              <span>{(displayName || '?').slice(0, 1).toUpperCase()}</span>
            </span>
            <div className="ct-preview-copy">
              <span className="ct-preview-platform">{blockedFollowProfile.platform || t('common.profile')}</span>
              <h2>
                {displayName}
                {blockedFollowProfile.is_verified ? <i className="ph-fill ph-seal-check"></i> : null}
              </h2>
              {blockedFollowProfile.handle ? <p>@{blockedFollowProfile.handle}</p> : null}
            </div>
          </div>

          <div className="ct-rights-notice">
            <span className="ct-rights-icon"><i className="ph ph-shield-warning"></i></span>
            <div>
              <p className="ct-kicker">{t('discover.blockedKicker')}</p>
              <h3>{t('discover.blockedTitle')}</h3>
              <p>{t('discover.blockedCopy', { name: displayName })}</p>
            </div>
          </div>

          <div className="ct-preview-stats">
            <span>
              <strong>{blockedFollowProfile.platform || 'n/d'}</strong>
              <em>{t('common.profile')}</em>
            </span>
            <span>
              <strong>{blockedFollowProfile.follower_count ? formatFollowers(blockedFollowProfile.follower_count) : t('discover.followersUnavailable')}</strong>
              <em>{t('discover.audience')}</em>
            </span>
            <span>
              <strong>{blockedFollowProfile.slug || blockedFollowProfile.handle || 'n/d'}</strong>
              <em>{t('discover.blockedReference')}</em>
            </span>
          </div>

          <div className="ct-preview-actions">
            {profileUrl ? (
              <a href={profileUrl} target="_blank" rel="noreferrer">
                {t('discover.openProfile')}
              </a>
            ) : null}
            <button type="button" onClick={() => setBlockedFollowProfile(null)}>
              {t('discover.blockedClose')}
            </button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="ct-discover-page">
      <div className="ct-discover-add-grid">
        <section className="ct-discover-hero ct-discover-method ct-discover-method-search">
          <div className="ct-discover-brand-row">
            <p className="ct-kicker">{t('app.discover')}</p>
            <span className="ct-brand-mark image">
              <img src="/brand/outfit-detective-mark-transparent.svg" alt="" />
            </span>
          </div>
          <h1>{t('discover.title')}</h1>
          <p>{t('discover.subtitle')}</p>
          <div className="ct-social-searchbox">
            <div className="ct-platform-switch">
              <button
                type="button"
                className={socialSearch.platform === 'instagram' ? 'active' : ''}
                onClick={() => setSocialSearch((current) => ({ ...current, platform: 'instagram' }))}
              >
                Instagram
              </button>
              <button
                type="button"
                className={socialSearch.platform === 'tiktok' ? 'active' : ''}
                onClick={() => setSocialSearch((current) => ({ ...current, platform: 'tiktok' }))}
              >
                TikTok
              </button>
            </div>
            <div className="ct-search-input-wrap">
              <i className="ph ph-magnifying-glass"></i>
              <input
                value={socialSearch.query}
                onChange={(event) => setSocialSearch((current) => ({ ...current, query: event.target.value }))}
                placeholder="@cristiano, rihanna, zendaya..."
                autoFocus
              />
              {socialSearch.query.trim().length >= 2 && (socialSearchPending || socialSearching) ? (
                <span className="ct-input-spinner" aria-label={t('discover.searching')}></span>
              ) : null}
            </div>
          </div>
          {message ? <div className="ct-toast" role="status">{message}</div> : null}
          {socialSearch.query.length >= 2 ? (
            <div className="ct-social-results">
              {!socialSearchPending && !socialSearching && socialResults.length === 0 ? (
                <p className="ct-muted">{t('discover.noVerified')}</p>
              ) : null}
              {socialResults.map((result) => renderSocialResult(result))}
              {socialHasMore ? (
                <div className="ct-load-sentinel" ref={loadMoreRef}>
                  {socialLoadingMore ? t('discover.loadingMore') : t('discover.loadMore')}
                </div>
              ) : null}
            </div>
          ) : null}
        </section>

        <section className="ct-client-tools single ct-discover-paste-wrap">
          <form className="ct-client-tool-card ct-discover-method ct-discover-method-paste" onSubmit={addProfileFromInput}>
            <div className="ct-discover-method-top">
              <span className="ct-discover-method-icon"><i className="ph ph-link-simple"></i></span>
              <div>
                <h2>{t('discover.pasteTitle')}</h2>
                <p>{t('discover.pasteCopy')}</p>
              </div>
            </div>
            <div className="ct-platform-switch ct-paste-platform" aria-label="Piattaforma profilo">
              <button
                type="button"
                className={pastePlatform === 'instagram' ? 'active' : ''}
                onClick={() => setPastePlatform('instagram')}
              >
                Instagram
              </button>
              <button
                type="button"
                className={pastePlatform === 'tiktok' ? 'active' : ''}
                onClick={() => setPastePlatform('tiktok')}
              >
                TikTok
              </button>
            </div>
            <div className="ct-inline-action">
              <input
                className="form-control"
                value={profileInput}
                onChange={(event) => updateProfileInput(event.target.value)}
                placeholder={t('discover.pastePlaceholder')}
              />
              <button className="btn btn-dark" disabled={profileChecking}>
                {profileChecking ? t('discover.checkingProfile') : t('discover.checkProfile')}
              </button>
            </div>
          </form>
        </section>
      </div>

      {renderProfilePreview()}
      {renderBlockedFollowDialog()}

      <section className="ct-import-panel">
        <button type="button" className="ct-secondary-toggle" onClick={() => setShowBulkImport((value) => !value)}>
          {showBulkImport ? t('discover.closeBulk') : t('discover.openBulk')}
        </button>
        {showBulkImport ? (
          <form className="ct-bulk-form" onSubmit={importBulkList}>
            <textarea
              className="form-control"
              rows="5"
              value={bulkInput}
              onChange={(event) => setBulkInput(event.target.value)}
              placeholder="@zendaya&#10;https://www.instagram.com/badgalriri/&#10;https://www.tiktok.com/@cristiano"
            />
            <button className="btn btn-dark">{t('discover.addProfiles')}</button>
          </form>
        ) : null}
      </section>

      <section className="ct-following-section">
        <div className="ct-section-title">
          <div>
            <p className="ct-kicker">{t('feed.following')}</p>
            <h2>{t('discover.yourList')}</h2>
          </div>
        </div>
        {follows.length === 0 ? (
          <div className="ct-card ct-empty">
            <h2>{t('discover.noFollowingTitle')}</h2>
            <p>{t('discover.noFollowingCopy')}</p>
          </div>
        ) : (
          <div className="ct-following-grid">
            {follows.map((item) => (
              <article className="ct-following-card" key={item.id}>
                <div className="ct-person-photo">
                  {item.image_url
                    ? <img src={avatarImageUrl(item.image_url)} alt={item.name} onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = ''; }} />
                    : null}
                  <span style={{ display: item.image_url ? 'none' : '' }}>{(item.name || '?').slice(0, 1).toUpperCase()}</span>
                </div>
                <div>
                  <Link to={`/following/${item.slug}`}>
                    <h2>{item.name}</h2>
                  </Link>
                </div>
                <div className="ct-following-actions">
                  <button type="button" onClick={() => openProfilePreview({
                    platform: item.platform,
                    handle: item.handle,
                    display_name: item.name,
                    avatar_url: item.image_url,
                    celebrity: { id: item.id, name: item.name },
                  })} disabled={!item.platform || !item.handle}>
                    {t('common.preview')}
                  </button>
                  <button type="button" onClick={() => removeFollow(item.id)}>
                    {t('common.unfollow')}
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default Discover;
